import { useNavigate } from 'react-router-dom';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import StoryViewerFrame from './components/storyViewer/StoryViewerFrame';
import StoryViewerPeek from './components/storyViewer/StoryViewerPeek';
import StoryViewerCarousel from './components/storyViewer/StoryViewerCarousel';
import { useStoryProgress } from './hooks/useStoryProgress';
import { useStoryMediaReady } from './hooks/useStoryMediaReady';
import {
	StoryViewerOverlay,
	StoryClose,
	StoryCenterWrap,
} from './StoryViewer.styles';

const StoryViewer = ({
	open,
	storyPersons,
	activePersonIdx,
	activeStoryIdx,
	slideState,
	slideAnimating,
	isSliding,
	onClose,
	onNextStory,
	onPrevStory,
	onPeekPrev,
	onPeekNext,
	onSlideComplete,
}) => {
	const navigate = useNavigate();
	const t = useLanguageData('home');
	const personCount = storyPersons.length;
	const activePerson = storyPersons[activePersonIdx];
	const currentItem = activePerson?.stories[activeStoryIdx];
	const person = activePerson?.person;

	const slideKey = `${activePersonIdx}-${activeStoryIdx}-${currentItem?.mediaId ?? 'none'}`;
	const isViewerActive =
		open &&
		personCount > 0 &&
		Boolean(activePerson && currentItem && person);

	const { mediaReady, isVideo, handleMediaReady } = useStoryMediaReady({
		active: isViewerActive,
		slideKey,
		isSliding,
	});

	const {
		isPaused,
		segmentProgress,
		storyPausedRef,
		pauseProgress,
		resumeProgress,
		handleVideoProgress,
		handleVideoEnded,
	} = useStoryProgress({
		open: isViewerActive,
		slideKey,
		onNext: onNextStory,
		mediaReady,
		paused: isSliding,
		isVideo,
	});

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleReply = (e) => {
		e.preventDefault();
		const input = e.currentTarget.querySelector('input');
		const text = input?.value?.trim();
		if (!text) {
			return;
		}
		input.value = '';
		onClose();
		navigate('/chat');
	};

	if (!isViewerActive) {
		return null;
	}

	const wrapPersonIdx = (idx) => (idx + personCount) % personCount;
	const prevPerson =
		personCount > 1 ? storyPersons[wrapPersonIdx(activePersonIdx - 1)] : null;
	const nextPerson =
		personCount > 1 ? storyPersons[wrapPersonIdx(activePersonIdx + 1)] : null;

	const leavingItem = slideState
		? storyPersons[slideState.fromIdx].stories[
				slideState.fromStoryIdx ?? activeStoryIdx
			]
		: currentItem;
	const enteringItem = slideState
		? storyPersons[slideState.toIdx].stories[slideState.enteringStoryIdx ?? 0]
		: null;

	const segmentCount = currentItem.storyCount || 1;
	const currentSegment = Math.max(0, (currentItem.storyNumber || 1) - 1);

	const buildFrame = (item, interactive, frameSlideKey = slideKey) => {
		const itemPerson = item.person;
		const itemSegmentCount = item.storyCount || 1;
		const itemSegment = Math.max(0, (item.storyNumber || 1) - 1);

		return (
			<StoryViewerFrame
				currentItem={item}
				person={itemPerson}
				segmentCount={itemSegmentCount}
				currentSegment={itemSegment}
				segmentProgress={interactive ? segmentProgress : 0}
				isPaused={interactive ? isPaused : true}
				pauseProgress={pauseProgress}
				resumeProgress={resumeProgress}
				storyPausedRef={storyPausedRef}
				slideKey={frameSlideKey}
				canPlay={interactive ? mediaReady : false}
				onMediaReady={interactive ? handleMediaReady : undefined}
				onVideoProgress={interactive ? handleVideoProgress : undefined}
				onVideoEnded={interactive ? handleVideoEnded : undefined}
				timeAgoLabel={t.storyTimeAgo}
				playLabel={t.storyPlay}
				pauseLabel={t.storyPause}
				onPrev={() => onPrevStory({ source: 'manual' })}
				onNext={() => onNextStory({ source: 'manual' })}
				prevLabel={t.storyPrev}
				nextLabel={t.storyNext}
				onReply={handleReply}
				replyPlaceholder={t.storyReplyPlaceholder}
				replySubmitLabel={t.storyReplySend}
				onClose={onClose}
				closeLabel={t.storyClose}
				interactive={interactive}
			/>
		);
	};

	const centerFrame = buildFrame(currentItem, !isSliding);
	const leavingFrame = buildFrame(leavingItem, false);
	const enteringFrame = enteringItem ? buildFrame(enteringItem, false) : null;

	return (
		<StoryViewerOverlay $open={open} onClick={handleOverlayClick}>
			<StoryClose type="button" onClick={onClose} aria-label={t.storyClose}>×</StoryClose>
			{prevPerson && (
				<StoryViewerPeek
					key={prevPerson.personId}
					item={prevPerson.peekItem}
					onClick={onPeekPrev}
					ariaLabel={t.storyPrev}
					hidden={isSliding}
				/>
			)}
			<StoryCenterWrap>
				<StoryViewerCarousel
					isSliding={isSliding}
					direction={slideState?.direction ?? 0}
					animating={slideAnimating}
					onTransitionEnd={onSlideComplete}
					leavingFrame={leavingFrame}
					centerFrame={centerFrame}
					enteringFrame={enteringFrame}
				/>
			</StoryCenterWrap>
			{nextPerson && (
				<StoryViewerPeek
					key={nextPerson.personId}
					item={nextPerson.peekItem}
					onClick={onPeekNext}
					ariaLabel={t.storyNext}
					hidden={isSliding}
				/>
			)}
		</StoryViewerOverlay>
	);
};

export default StoryViewer;
